import {approvalApi} from '@api/approval/api';
import {fieldApi} from '@api/field/api';
import type {ApprovalView} from '@api/approval/api.d';
import type {FieldView} from '@api/field/api.d';
import type {ApiCallOptions} from '@api/types';

interface ApprovalsAndFields {
    approvalsByDocumentId: Record<string, ApprovalView[]>;
    fieldsByApprovalId: Record<string, FieldView[]>;
}

function getApprovalsByDocumentId(approvals: ApprovalView[], documentIds: string[]): Record<string, ApprovalView[]> {
    const approvalsByDocumentId: Record<string, ApprovalView[]> = {};

    documentIds.forEach((documentId) => {
        approvalsByDocumentId[documentId] = [];
    });

    approvals.forEach((approval) => {
        if (!approvalsByDocumentId[approval.document_id]) {
            approvalsByDocumentId[approval.document_id] = [];
        }

        approvalsByDocumentId[approval.document_id].push(approval);
    });

    return approvalsByDocumentId;
}

function getFieldsByApprovalId(fields: FieldView[], approvals: ApprovalView[]): Record<string, FieldView[]> {
    const fieldsByApprovalId: Record<string, FieldView[]> = {};

    approvals.forEach((approval) => {
        fieldsByApprovalId[approval.id] = [];
    });

    fields.forEach((field) => {
        if (fieldsByApprovalId[field.approval_id]) {
            fieldsByApprovalId[field.approval_id].push(field);
        }
    });

    return fieldsByApprovalId;
}

export async function getApprovalsForTransactionByDocumentId(
    packageId: string,
    documentIds: string[],
    options: ApiCallOptions = {}
): Promise<Record<string, ApprovalView[]>> {
    const response = await approvalApi.getApprovalsForTransaction(packageId, options).catch((error) => {
        console.error(`Failed to fetch approvals for transaction ${packageId}:`, error);
        return null;
    });

    return getApprovalsByDocumentId((response?.data as unknown as ApprovalView[]) || [], documentIds);
}

export async function getApprovalsAndFieldsForTransaction(
    packageId: string,
    documentIds: string[],
    options: ApiCallOptions = {}
): Promise<ApprovalsAndFields> {
    const [approvalsResponse, fieldsResponse] = await Promise.all([
        approvalApi.getApprovalsForTransaction(packageId, options).catch((error) => {
            console.error(`Failed to fetch approvals for transaction ${packageId}:`, error);
            return null;
        }),
        fieldApi.getFieldsForTransaction(packageId, options).catch((error) => {
            console.error(`Failed to fetch fields for transaction ${packageId}:`, error);
            return null;
        })
    ]);

    const approvals = (approvalsResponse?.data as unknown as ApprovalView[]) || [];
    const fields = (fieldsResponse?.data as unknown as FieldView[]) || [];

    return {
        approvalsByDocumentId: getApprovalsByDocumentId(approvals, documentIds),
        fieldsByApprovalId: getFieldsByApprovalId(fields, approvals)
    };
}

export async function getApprovalsAndFieldsForDocument(
    packageId: string,
    documentId: string,
    options: ApiCallOptions = {}
): Promise<ApprovalsAndFields> {
    const [approvals, fieldsResponse] = await Promise.all([
        approvalApi.getAllApprovals(packageId, documentId, {}, options),
        fieldApi.getFieldsForDocument(packageId, documentId, options)
    ]);

    const documentApprovals = approvals || [];
    const fields = (fieldsResponse?.data as unknown as FieldView[]) || [];

    return {
        approvalsByDocumentId: {[documentId]: documentApprovals},
        fieldsByApprovalId: getFieldsByApprovalId(fields, documentApprovals)
    };
}
